import { ChangeDetectionStrategy, Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { API, DealDTO, SSPContractDTO } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { DiscussionService } from '../../../services/discussion.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { QueryParamService } from '../../../services/lists/query-params';
import { DealCreationComponent } from '../../../components/deal-creation/deal-creation.component';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { RequestClass } from '../../../services/global/RequestClass';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButton } from '../../../services/global/global-interfaces';

type SplitButtonAction = 'edit' | 'delete';

@Component({
  selector: 'sb-page-deals',
  templateUrl: './deals.component.html',
  styleUrls: ['./deals.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealsComponent extends RequestClass {
  public readonly util = inject(UtilService);
  public readonly discussionService = inject(DiscussionService);
  public readonly queryParamService = inject(QueryParamService);
  public readonly permissionService = inject(PermissionService);
  protected readonly api = inject(API);
  private readonly errorHandler = inject(ErrorService);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // table wrapper variables
  deals: DealDTO[] = [];

  // filter variables
  filteredSSPContracts: SSPContractDTO[] = [];
  queryParamsParsed: boolean = false;
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .shared_Deal_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          ssp_contract_id: this.filteredSSPContracts ? this.util.arrayToIdArray(this.filteredSSPContracts) : null,
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (receivedDeals: DealDTO[]) => {
          this.deals = this.buildSplitButtons(receivedDeals);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  createDeal() {
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: DealCreationComponent,
          data: {
            sspContract: this.getPreFilteredContracts(),
            useParamData: true,
            editMode: false,
          },
        },
        title: this.translate.instant('lang.globals.createContract'),
        icon: 'plus',
        // modalOptions: {size: 'sm'}
      })
      .then((e: SSPContractDTO) => {
        if (e) {
          this.requestDebounced();
        }
      })
      .catch(() => {});
  }

  getPreFilteredContracts(): SSPContractDTO {
    return this.filteredSSPContracts?.length > 0 ? this.filteredSSPContracts[0] : null;
  }

  deleteDeal(deal: DealDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.deleteDeal'),
        message: this.translate.instant('lang.globals.deleteDealMessage', { value: deal.identifier }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .shared_Deal_deleteDeal(deal.id)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.requestDebounced();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  editDeal(pDeal: DealDTO) {
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: DealCreationComponent,
          data: {
            deal: pDeal,
            useParamData: true,
            editMode: true,
          },
        },
        title: this.translate.instant('lang.globals.editDeal'),
        icon: 'edit',
        // modalOptions: {size: 'sm'}
      })
      .then((e: SSPContractDTO) => {
        if (e) {
          this.requestDebounced();
        }
      })
      .catch(() => {});
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.deal_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          action: 'edit',
          tooltip: 'lang.globals.editDeal',
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.deal_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.delete',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(deal: DealDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'edit':
        this.editDeal(deal);
        break;
      case 'delete':
        this.deleteDeal(deal);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.filteredSSPContracts = filterMap.get('sspContracts');
  }
}
